import React from 'react'
import PropTypes from 'prop-types'

/**
 * StandingsTable Component
 *
 * Displays team standings with Win/Loss/Tie records and statistics.
 * Used in LeagueDetails (Standings tab), Home (league display), and Dashboard (manager view).
 *
 * @param {Object} props
 * @param {Array} props.standings - Array of standings data with team info and stats
 * @param {boolean} [props.showExtendedStats=true] - Show GF/GA columns (default: true)
 * @param {boolean} [props.compact=false] - Use compact view with limited columns (default: false)
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {number} [props.limit] - Limit number of rows displayed
 * @param {Function} [props.onTeamClick] - Callback when team is clicked
 */
const StandingsTable = React.memo(({
  standings = [],
  showExtendedStats = true,
  compact = false,
  className = '',
  limit,
  onTeamClick
}) => {
  if (!standings || standings.length === 0) {
    return null
  }

  const displayStandings = limit ? standings.slice(0, limit) : standings

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Team</th>
            {!compact && <th className="text-center hidden lg:table-cell">GP</th>}
            <th className="text-center">W</th>
            <th className="text-center">L</th>
            <th className="text-center hidden sm:table-cell">T</th>
            {showExtendedStats && (
              <>
                <th className="text-center hidden sm:table-cell">GF</th>
                <th className="text-center hidden sm:table-cell">GA</th>
                <th className="text-center hidden lg:table-cell">DIFF</th>
              </>
            )}
            <th className="text-center">PTS</th>
          </tr>
        </thead>
        <tbody>
          {displayStandings.map((standing, index) => {
            const teamName = standing.team?.name || standing.team_name
            const teamColor = standing.team?.color || standing.team_color
            const teamId = standing.team?.id || standing.team_id
            const gamesPlayed = standing.games_played || (standing.wins + standing.losses + standing.ties)
            const goalsFor = standing.gf !== undefined ? standing.gf : standing.goals_for
            const goalsAgainst = standing.ga !== undefined ? standing.ga : standing.goals_against
            const differential = standing.differential !== undefined ? standing.differential : (goalsFor - goalsAgainst)

            return (
              <tr
                key={teamId || index}
                className={onTeamClick ? 'cursor-pointer' : ''}
                onClick={onTeamClick ? () => onTeamClick(standing) : undefined}
              >
                <td className="font-semibold text-slate-900">{index + 1}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: teamColor || '#94a3b8' }}
                    />
                    <span className="font-medium text-slate-900">{teamName}</span>
                  </div>
                </td>
                {!compact && (
                  <td className="text-center hidden lg:table-cell text-slate-600">
                    {gamesPlayed}
                  </td>
                )}
                <td className="text-center text-slate-700">{standing.wins}</td>
                <td className="text-center text-slate-700">{standing.losses}</td>
                <td className="text-center hidden sm:table-cell text-slate-700">
                  {standing.ties}
                </td>
                {showExtendedStats && (
                  <>
                    <td className="text-center hidden sm:table-cell text-slate-600">
                      {goalsFor}
                    </td>
                    <td className="text-center hidden sm:table-cell text-slate-600">
                      {goalsAgainst}
                    </td>
                    <td className={`text-center hidden lg:table-cell font-medium ${
                      differential > 0 ? 'text-emerald-600' : differential < 0 ? 'text-red-600' : 'text-slate-600'
                    }`}>
                      {differential > 0 ? '+' : ''}{differential}
                    </td>
                  </>
                )}
                <td className="text-center font-bold text-ice-600">
                  {standing.points}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
})

StandingsTable.displayName = 'StandingsTable'

StandingsTable.propTypes = {
  standings: PropTypes.arrayOf(PropTypes.shape({
    team: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      color: PropTypes.string,
    }),
    team_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    team_name: PropTypes.string,
    team_color: PropTypes.string,
    wins: PropTypes.number,
    losses: PropTypes.number,
    ties: PropTypes.number,
    points: PropTypes.number,
    games_played: PropTypes.number,
    gf: PropTypes.number,
    goals_for: PropTypes.number,
    ga: PropTypes.number,
    goals_against: PropTypes.number,
    differential: PropTypes.number,
  })),
  showExtendedStats: PropTypes.bool,
  compact: PropTypes.bool,
  className: PropTypes.string,
  limit: PropTypes.number,
  onTeamClick: PropTypes.func,
}

export default StandingsTable
